/**
 * Helper per la verifica della validità del token di sessione
 */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { appCommonConstants } from '../app.constants';
import { AppAuthService } from '../services/auth.service';
import { AppAuthDataService } from '../services/authData.service';
import { AppEventService } from '../services/event.service';
import { AppLoadingService } from '../services/loading.service';
import { AppLoggerService } from '../services/logger.service';

@Injectable({
  providedIn: 'root',
})

/**
 * Servizio per la gestione autorizzazione alla navigazione
 * Ref: https://angular.io/api/router/CanActivate
 */
export class AppAuthVerifyGuard implements CanActivate, CanActivateChild {
  constructor(
    private appAuthData: AppAuthDataService,
    private appAuthService: AppAuthService,
    private eventService: AppEventService,
    private loading: AppLoadingService,
    private logger: AppLoggerService,
    private translate: TranslateService
  ) { }

  /**
   * https://angular.io/api/router/CanActivate
   * @param next
   * @param state
   */
  public canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    let isLoading = true;
    let timeoutHandler = setTimeout(() => {
      isLoading = true;
      this.loading.show();
    }, 750); // Per poter visualizzare il loader durante richieste che durano "troppo tempo"
    return this.appAuthService.checkLoggedIn()
      .pipe(
        finalize(() => {
          if (timeoutHandler) {
            clearTimeout(timeoutHandler);
          }
          if (isLoading) {
            this.loading.hide();
          }
        }),
        tap(() => {
          // IMPORTANTE: invocare l'evento alreadyLoggedin necessario in caso di reload della pagina, setTimeout necessario affinché il giro si completi dopo l'inizializzazione di signalr
          setTimeout(() => this.eventService.broadcast(appCommonConstants.events.alreadyLoggedin, null));
        }),
        map((x) => {
          return x;
        }),
        catchError((err) => {
          if (err.name !== 'BrowserAuthError') {  // Catch di eventuale errore di redirect in autenticazione tramite AzureAd
            this.logger.error({ message: this.translate.instant("MESSAGE.BEARERTOKEN.INVALID", { path: state.url }) });
          }
          this.appAuthData.authDataRemove();
          this.appAuthService.gotoLogin(state.url);
          return of(false);
        })
      );
  }

  /**
   * https://angular.io/api/router/CanActivateChild
   * @param childRoute
   * @param state
   */
  public canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return this.appAuthService.checkLoggedIn().pipe(
      map(x => {
        return x;
      }),
      catchError(() => {
        this.logger.error({ message: this.translate.instant("MESSAGE.BEARERTOKEN.INVALID", { path: state.url }) });
        return of(false);
      })
    );
  }
}
